import { Outlet, Link } from 'react-router-dom';
import { useTheme } from 'next-themes';
import { SidebarProvider, SidebarInset, SidebarTrigger } from '@/components/ui/sidebar';
import { AppSidebar } from './AppSidebar';
import ThemeBackground from './ThemeBackground';
import AppFooter from './AppFooter';
import MilestoneOverlay from '@/components/dashboard/MilestoneOverlay';
import { useCinemaMode } from '@/contexts/CinemaModeContext';
import { useBookingNotifications } from '@/hooks/useBookingNotifications';
import { useTabAccent } from '@/hooks/useTabAccent';
import { useNavStyle } from '@/hooks/useNavStyle';
import { useGoogie } from '@/hooks/useGoogie';
import { GoogieShell } from '@/components/googie/GoogieShell';
import { AppApertureBar } from './AppApertureBar';
// GAL-8 — persistent upload pill/tray. Lives in the CHROME (not in
// any gallery page) because uploads now run in the module-level
// manager and must stay visible across route changes.
import { UploadTray } from '@/components/uploads/UploadTray';
import { Menu } from 'lucide-react';
import { OrbitIcon } from "@/components/icons/OrbitIcon";
// Wave 13a — Light/dark mode re-introduced; logo swaps with mode.
import orbitLogoWhite from '@/assets/orbit-retro-logo-white.png';
import orbitLogoBlack from '@/assets/orbit-retro-logo-black.png';
import { useThemeMode } from '@/hooks/useThemeMode';
// POL-12 — brand mark hoisted out of the Aperture pill (used by
// `ApertureBrandMark` below).  Paid-tier custom logos win via
// `useStudioBranding()`; otherwise the retro Orbit wordmark swaps
// per theme mode (white on dark, black on light).
import { useStudioBranding } from '@/hooks/useStudioBranding';

const RootLayout = () => {
  const { cinemaMode } = useCinemaMode();
  const { resolvedTheme } = useTheme();
  // Wave 13a — logo swaps with theme mode so light mode gets the
  // dark logotype on the ivory canvas, dark mode keeps the white
  // logotype on the cosmic canvas.
  const { mode } = useThemeMode();
  const logoSrc = mode === 'dark' ? orbitLogoWhite : orbitLogoBlack;

  // #494 Pass 4 — Chrome preference: sidebar (classic left rail)
  // or aperture (bottom Aperture Bar).  Chosen in the welcome
  // sequence's showcase slide or Settings → Navigation.
  const [navStyle] = useNavStyle();
  const useAperture = navStyle === 'aperture';
  const [googie] = useGoogie();
  // GOOGIE-9 — the redesign obeys the studio's own light/dark choice
  // now. Before this it forced dark and the Appearance toggle was a
  // switch wired to nothing.
  const googieLight = mode === 'light';

  // #357 — subscribe to realtime booking inserts once at the shell
  // level. Fires a toast wherever the studio is in the app when a
  // client submits /book/:slug. Runs no-op if orgId isn't ready.
  useBookingNotifications();

  // #494 — Aperture-style per-tab ambient tinting. Sets a
  // `data-orbit-tab` attribute on <html> based on the current
  // route so the ambient body backdrop's radial glows shift to
  // the destination's identity colour (nova blue on chat,
  // brass on passport, indigo on projects, …).  Mirrors iOS
  // OrbitAmbient's per-destination accents.
  useTabAccent();

  // GOOGIE-2 — the redesign's shell is hoisted HERE, from the ten
  // screens up to the layout. That is the move that makes retiring
  // the old design possible: every route now lands under the sky
  // with the Aperture Bar beneath it, whether or not anyone has
  // rebuilt that particular page yet. A redesigned screen wraps its
  // content in `GoogieScreen` and wears the new type and colour; a
  // page nobody has touched keeps its own palette and simply gains
  // the backdrop and the navigation, which is old — not broken.
  //
  // The app's own chrome (sidebar / theme backdrop / footer) steps
  // aside; the Aperture Bar does not, because it is Orbit's
  // navigation and the redesign has no business replacing a control
  // studios have already learned. The flag lives in Settings →
  // Appearance; off, this file behaves exactly as it did.
  if (googie) {
    // GOOGIE-6 — the chrome preference survives the redesign. Rennan:
    // "we also need to restore the side rail with no glow for when
    // that's selected." A studio who chose the sidebar in Settings →
    // Appearance chose it for a reason, and turning on a new look
    // shouldn't quietly take their navigation away. So: sidebar mode
    // gets the rail and a `bare` shell — no Aperture Bar, and
    // therefore no aura, because the aura belongs to the bar.
    if (!useAperture) {
      return (
        <SidebarProvider>
          <GoogieShell bare light={googieLight}>
            <div className="flex min-h-svh w-full">
              <AppSidebar />
              <SidebarInset className="flex min-h-0 flex-1 flex-col overflow-y-auto overflow-x-hidden bg-transparent !m-0 !rounded-none !shadow-none">
                {/* The rail is unreachable on phones without this —
                    the trigger lives inside the sidebar itself. */}
                <div className="flex items-center gap-2 px-5 pt-5 md:hidden">
                  <SidebarTrigger>
                    <Menu className="h-5 w-5" />
                  </SidebarTrigger>
                </div>
                <Outlet />
              </SidebarInset>
            </div>
          </GoogieShell>
          <MilestoneOverlay />
          <UploadTray />
        </SidebarProvider>
      );
    }

    return (
      <GoogieShell light={googieLight}>
        <Outlet />
        <MilestoneOverlay />
        <UploadTray />
      </GoogieShell>
    );
  }

  return (
    <SidebarProvider>
      <div className="flex min-h-svh w-full overflow-hidden relative">
        <ThemeBackground />
        {/* #494 Pass 4 — Chrome swap.  Sidebar mode renders the
            classic left rail; Aperture mode hides the rail and
            renders the bottom Aperture Bar (see AppApertureBar).
            SidebarProvider stays wrapped either way so hooks
            depending on it (mobile trigger, etc.) still work. */}
        {!useAperture && <AppSidebar />}
        <SidebarInset
          className={
            useAperture
              ? // POL-14 — Aperture + dark mode: kill every trace of a
                // wrapping "box" so the Today gradient + stars bleed
                // edge-to-edge.  Light mode keeps the shadcn defaults
                // (subtle rounded card + margin) since Rennan wants the
                // box there.  `!` beats shadcn's peer-data selectors.
                'flex-1 min-h-0 overflow-y-auto overflow-x-hidden flex flex-col relative z-[1] bg-transparent pb-24 dark:!m-0 dark:!rounded-none dark:!shadow-none'
              : 'flex-1 min-h-0 overflow-y-auto overflow-x-hidden flex flex-col relative z-[1] bg-transparent dark:!m-0 dark:!rounded-none dark:!shadow-none'
          }
        >
          {/* Mobile top bar — only renders below md. Without this, the
              sidebar is unreachable on phones because SidebarTrigger
              lives inside the sidebar itself. */}
          {/* Wave 13a — theme-aware chrome.  In light mode the top
              bar picks up the light card token; in dark it stays
              on the translucent ink fill.
              CHROME-1 — the hand-rolled dark fill + hairline were
              replaced by the `-strip` variant of the house material,
              so this bar, the rail and the menus all frost the same
              way.  The dark:bg-[…] utility had to go: a `dark:`
              arbitrary background is the same specificity as the
              `.new-shell .orbit-glass` rule and ships later in the
              stylesheet, so it would have won and left the strip
              flat again. */}
          <header className="md:hidden flex items-center justify-between px-4 h-14 border-b border-border bg-card/85 orbit-glass orbit-glass-strip orbit-glass-frosted sticky top-0 z-30">
            <SidebarTrigger
              aria-label="Open menu"
              className="-ml-2 inline-flex items-center justify-center h-9 w-9 rounded-md hover:bg-muted dark:hover:bg-white/[0.06] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              <OrbitIcon name="line.3.horizontal" fallback={Menu} className="w-5 h-5" />
            </SidebarTrigger>
            <Link to="/" className="flex items-center">
              <img src={logoSrc} alt="Orbit" className="h-7 w-auto" />
            </Link>
            {/* Spacer to balance the trigger so the logo stays centered. */}
            <span className="w-9" aria-hidden />
          </header>
          {/* Desktop top strip — kept translucent so the ambient still
              bleeds through the top edge of the frame.  In aperture
              mode the sidebar-toggle is hidden (nothing to toggle).
              CHROME-1 — same `-strip` material as the mobile bar
              above; see the note there for why the dark:bg-[…]
              utility was removed rather than left alongside. */}
          {!useAperture && (
            <header className="hidden md:flex items-center gap-2 h-11 px-4 sticky top-0 z-20 border-b border-border bg-card/70 orbit-glass orbit-glass-strip orbit-glass-frosted">
              <SidebarTrigger
                aria-label="Toggle sidebar"
                className="inline-flex items-center justify-center h-8 w-8 rounded-md hover:bg-muted/60 text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              />
            </header>
          )}
          {/* EMAIL-4 · Bug 2 — In Aperture mode the fixed
              ApertureBrandMark (top-left, z-40, ~104px including
              inset+height) overlapped every page's own top-of-column
              eyebrow/heading (e.g. EMAILS on /emails).  Push the
              page content down by the logo's footprint on md+ so no
              page renders behind the fixed mark.  Mobile is unaffected
              (ApertureBrandMark is `hidden md:block`, mobile uses the
              centered header in the md:hidden strip above).  Sidebar
              mode already has an h-11 top strip so no fixed mark
              exists — no padding needed there.  Today's TodayPageBackdrop
              uses `absolute inset-0` on its own root inside this
              padded wrapper, so its backdrop still fills the visible
              content column edge-to-edge; the logo just sits in a
              cleared band above it. */}
          <div className={useAperture ? 'flex-1 md:pt-20' : 'flex-1'}>
            <Outlet />
          </div>
          <AppFooter />
        </SidebarInset>
        {/* Wave 10 — NovaFloating (bottom-right ambient companion)
            removed on web. Nova already has /assistant and the
            Aperture Bar tab; the floater added visual noise. */}
        <MilestoneOverlay />
        {/* GAL-8 — background-upload progress. Floats above the
            Aperture bar; renders nothing while no batch exists. */}
        <UploadTray />
        {/* #494 Pass 4 — Aperture Bar chrome. Rendered outside the
            SidebarInset so it floats over any page content. */}
        {useAperture && <AppApertureBar />}
        {/* POL-12 — brand mark pinned to the top-left of the screen
            in Aperture mode.  Coupled to the same nav-style gate as
            the bar itself so the two chromes always ship together
            (sidebar mode already renders the logo at the top of its
            column, so no top-left mark there).  Hidden on mobile —
            the existing md:hidden top header already centers a logo,
            and stacking a fixed logo over the sidebar-trigger would
            collide on 375px viewports. */}
        {useAperture && <ApertureBrandMark />}
      </div>
    </SidebarProvider>
  );
};

/**
 * POL-12 — Aperture-mode brand mark, pinned to the top-left of the
 * viewport.  Mounted from `RootLayout` so it stays coupled to the
 * Aperture Bar gate (both render iff `useNavStyle() === 'aperture'`).
 *
 *   • Paid-tier studios with an uploaded custom logo see their mark
 *     (via `useStudioBranding()`); otherwise the retro Orbit
 *     wordmark, swapped per theme mode.
 *   • ~28px tall — comparable to the sidebar's collapsed logo.
 *   • Tap → `/` (Today).
 *   • `md:block hidden` — desktop only.  Mobile already renders a
 *     centered logo inside the top header (`md:hidden` block above).
 *   • `z-30` — above page content, below any modals / dialogs.
 */
function ApertureBrandMark() {
  const { mode } = useThemeMode();
  // BRAND-2 — `customLogoUrl` is already theme-resolved inside the
  // hook (dark → dark ?? legacy; light → light ?? dark ?? legacy),
  // so this mount stays a plain swap-in with zero variant logic.
  const branding = useStudioBranding();
  const fallbackSrc = mode === 'dark' ? orbitLogoWhite : orbitLogoBlack;
  const logoSrc =
    branding.usesCustomLogo && branding.customLogoUrl
      ? branding.customLogoUrl
      : fallbackSrc;
  const logoAlt = branding.usesCustomLogo ? 'Your business — go to Today' : 'Orbit — go to Today';
  return (
    <Link
      to="/"
      aria-label={logoAlt}
      // POL-15 — bigger + more breathing room from the corner.  z-40
      // keeps it above chrome/backdrop; md+ only so mobile keeps its
      // own centered logo in the md:hidden top header.
      className="hidden md:block fixed top-7 left-7 z-40 rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
    >
      <img
        src={logoSrc}
        alt={logoAlt}
        className="h-12 w-auto select-none pointer-events-none"
        draggable={false}
      />
    </Link>
  );
}

export default RootLayout;